import { LocalDate } from '@js-joda/core';
import { groupBy } from 'ramda';
import { i18n } from 'i18n';
import PCSpool from '../MineModel/Spools/PCSpool';
import { isSpoolActive, spoolDisplayName } from '../MineModel/Spools/utils';
import { escapeCsvValue } from '../EditorPanelPage/components/ImportExportDialogs/helpers';

export type SpoolField = 'pipe' | 'level' | 'area' | 'status' | 'installed' | 'removed' | 'direction' | 'name' | 'other';
export type SpoolMeasure = 'length' | 'flowLoop' | 'count';
export type DateGranularity = 'year' | 'month' | 'day';
export type MeasureTotals = Record<SpoolMeasure, number>;

export interface SpoolPivotConfig {
  rows: SpoolField[];
  column: SpoolField | null;
  measures: SpoolMeasure[];
  dateGranularity: DateGranularity;
}

export interface PivotGroup {
  key: string;
  field: SpoolField;
  value: string;
  depth: number;
  cells: Record<string, MeasureTotals>;
  total: MeasureTotals;
  children: PivotGroup[];
}

export interface SpoolPivot {
  columns: string[];
  groups: PivotGroup[];
  grandTotal: { cells: Record<string, MeasureTotals>; total: MeasureTotals };
}

export const SPOOL_FIELDS: SpoolField[] = ['installed', 'removed', 'pipe', 'level', 'area', 'status', 'direction', 'name', 'other'];
export const SPOOL_MEASURES: SpoolMeasure[] = ['length', 'flowLoop', 'count'];
export const DATE_FIELDS: SpoolField[] = ['installed', 'removed'];
export const LENGTH_MEASURES: SpoolMeasure[] = ['length', 'flowLoop'];

export const spoolFieldLabel = (field: SpoolField): string => ({
  pipe: i18n._('Pipe Type'),
  level: i18n._('Level'),
  area: i18n._('Area'),
  status: i18n._('Status'),
  installed: i18n._('Installed Date'),
  removed: i18n._('Removed Date'),
  direction: i18n._('Direction'),
  name: i18n._('Name'),
  other: i18n._('Other'),
})[field];

// An empty bucket is "no value"; dates get their own wording because an undated spool is common.
export const bucketLabel = (field: SpoolField, value: string): string => {
  if (value) return value;
  return DATE_FIELDS.includes(field) ? i18n._('No date') : i18n._('None');
};

const dateBucket = (date: LocalDate | undefined, granularity: DateGranularity): string => {
  if (!date) return '';
  const iso = date.toString();
  if (granularity === 'year') return iso.slice(0, 4);
  if (granularity === 'month') return iso.slice(0, 7);
  return iso;
};

const directionOf = (spool: PCSpool): string => {
  if (spool.allowFlowAB && spool.allowFlowBA) return i18n._('Bidirectional');
  if (spool.allowFlowBA) return i18n._('Backward');
  return i18n._('Forward');
};

const FIELD_VALUE: Record<SpoolField, (spool: PCSpool, granularity: DateGranularity) => string> = {
  pipe: (spool) => spool.pipe?.name ?? '',
  level: (spool) => spool.level ?? '',
  area: (spool) => spool.area ?? '',
  status: (spool) => (isSpoolActive(spool) ? i18n._('Active') : i18n._('Inactive')),
  installed: (spool, granularity) => dateBucket(spool.installedOn, granularity),
  removed: (spool, granularity) => dateBucket(spool.removedOn, granularity),
  direction: directionOf,
  name: spoolDisplayName,
  other: (spool) => spool.other ?? '',
};

export const spoolFieldValue = (spool: PCSpool, field: SpoolField, granularity: DateGranularity): string => FIELD_VALUE[field](spool, granularity);

const MEASURE_VALUE: Record<SpoolMeasure, (spool: PCSpool) => number> = {
  length: (spool) => spool.length(),
  flowLoop: (spool) => spool.chokeLength ?? 0,
  count: () => 1,
};

const sumMeasures = (spools: PCSpool[]): MeasureTotals => ({
  length: spools.reduce((total, spool) => total + MEASURE_VALUE.length(spool), 0),
  flowLoop: spools.reduce((total, spool) => total + MEASURE_VALUE.flowLoop(spool), 0),
  count: spools.length,
});

// Natural order (900 before 1200, months in date order), with the "no value" bucket last.
export const sortBuckets = (values: string[]): string[] => [...values].sort((a, b) => {
  if (!a || !b) return Number(!a) - Number(!b);
  return a.localeCompare(b, undefined, { numeric: true });
});

const bucketBy = (spools: PCSpool[], field: SpoolField, granularity: DateGranularity): Record<string, PCSpool[]> => (
  groupBy((spool: PCSpool) => spoolFieldValue(spool, field, granularity), spools) as Record<string, PCSpool[]>
);

const cellsFor = (spools: PCSpool[], config: SpoolPivotConfig): Record<string, MeasureTotals> => {
  if (!config.column) return {};
  const buckets = bucketBy(spools, config.column, config.dateGranularity);
  return Object.fromEntries(Object.entries(buckets).map(([value, members]) => [value, sumMeasures(members)]));
};

const buildGroups = (spools: PCSpool[], rows: SpoolField[], path: string[], config: SpoolPivotConfig): PivotGroup[] => {
  if (rows.length === 0) return [];
  const [field, ...rest] = rows;
  const buckets = bucketBy(spools, field, config.dateGranularity);
  return sortBuckets(Object.keys(buckets)).map((value) => {
    const members = buckets[value];
    const groupPath = [...path, value];
    return {
      key: JSON.stringify(groupPath),
      field,
      value,
      depth: path.length,
      cells: cellsFor(members, config),
      total: sumMeasures(members),
      children: buildGroups(members, rest, groupPath, config),
    };
  });
};

export const buildSpoolPivot = (spools: PCSpool[], config: SpoolPivotConfig): SpoolPivot => ({
  columns: config.column
    ? sortBuckets(Array.from(new Set(spools.map((spool) => spoolFieldValue(spool, config.column as SpoolField, config.dateGranularity)))))
    : [],
  groups: buildGroups(spools, config.rows, [], config),
  grandTotal: { cells: cellsFor(spools, config), total: sumMeasures(spools) },
});

export interface PivotLayout {
  rows: SpoolField[];
  column: SpoolField | null;
}

export type PivotDropTarget = { area: 'rows'; index: number } | { area: 'columns' } | { area: 'fields' };

// A field lives in one place at a time: dropping it anywhere takes it out of where it was first. A rows index is
// the slot it lands in among the rows as they were, so dragging a level later in the list does not overshoot.
export const moveField = (layout: PivotLayout, field: SpoolField, target: PivotDropTarget): PivotLayout => {
  const from = layout.rows.indexOf(field);
  const rows = layout.rows.filter((existing) => existing !== field);
  const column = layout.column === field ? null : layout.column;
  if (target.area === 'columns') return { rows, column: field };
  if (target.area === 'fields') return { rows, column };
  const index = from !== -1 && from < target.index ? target.index - 1 : target.index;
  return { rows: [...rows.slice(0, index), field, ...rows.slice(index)], column };
};

// Every group in display order, skipping the children of collapsed ones.
export const visibleGroups = (groups: PivotGroup[], collapsed: Set<string> = new Set()): PivotGroup[] => groups.flatMap((group) => [
  group,
  ...(collapsed.has(group.key) ? [] : visibleGroups(group.children, collapsed)),
]);

export interface PivotChartSeries {
  name: string;
  values: (number | null)[];
}

// One series per column value for the top-level groups. The chart's palette has a fixed number of colours, so past
// that the largest values keep their own series (in their usual order) and the rest fold into "Other".
export const pivotChartSeries = (
  pivot: SpoolPivot,
  column: SpoolField,
  maxSeries: number,
  valueOf: (totals: MeasureTotals) => number,
): PivotChartSeries[] => {
  const largestFirst = [...pivot.columns].sort((a, b) => valueOf(pivot.grandTotal.cells[b]) - valueOf(pivot.grandTotal.cells[a]));
  const kept = pivot.columns.length <= maxSeries ? pivot.columns : largestFirst.slice(0, maxSeries - 1);
  const folded = pivot.columns.filter((value) => !kept.includes(value));
  const valuesFor = (columns: string[]) => pivot.groups.map((group) => {
    const present = columns.filter((value) => group.cells[value]);
    return present.length > 0 ? present.reduce((total, value) => total + valueOf(group.cells[value]), 0) : null;
  });
  return [
    ...pivot.columns.filter((value) => kept.includes(value)).map((value) => ({ name: bucketLabel(column, value), values: valuesFor([value]) })),
    ...(folded.length > 0 ? [{ name: i18n._('Other'), values: valuesFor(folded) }] : []),
  ];
};

export interface MeasureFormat {
  label: (measure: SpoolMeasure) => string;
  value: (measure: SpoolMeasure, totals: MeasureTotals | undefined) => number;
}

// One header row then one row per group (with its subtotal) and a grand total: what Copy and Export CSV write.
export const pivotTable = (pivot: SpoolPivot, config: SpoolPivotConfig, format: MeasureFormat): (string | number)[][] => {
  const columnHeaders = pivot.columns.flatMap((column) => config.measures.map(
    (measure) => `${bucketLabel(config.column as SpoolField, column)} - ${format.label(measure)}`,
  ));
  const totalHeaders = config.measures.map((measure) => (config.column ? `${i18n._('Total')} - ${format.label(measure)}` : format.label(measure)));
  const valuesOf = (cells: Record<string, MeasureTotals>, total: MeasureTotals) => [
    ...pivot.columns.flatMap((column) => config.measures.map((measure) => format.value(measure, cells[column]))),
    ...config.measures.map((measure) => format.value(measure, total)),
  ];
  // A subtotal row names its own group and those above it, leaving the deeper levels blank.
  const labelsOf = (group: PivotGroup): string[] => {
    const path = (JSON.parse(group.key) as string[]).map((value, depth) => bucketLabel(config.rows[depth], value));
    return config.rows.map((_, depth) => path[depth] ?? '');
  };

  return [
    [...config.rows.map(spoolFieldLabel), ...columnHeaders, ...totalHeaders],
    ...visibleGroups(pivot.groups).map((group) => [...labelsOf(group), ...valuesOf(group.cells, group.total)]),
    [
      ...config.rows.map((_, depth) => (depth === 0 ? i18n._('Grand Total') : '')),
      ...valuesOf(pivot.grandTotal.cells, pivot.grandTotal.total),
    ],
  ];
};

// The unpivoted list, one row per spool, for anyone who would rather pivot it in Excel.
export const spoolListTable = (spools: PCSpool[], format: MeasureFormat): (string | number)[][] => {
  const totalsOf = (spool: PCSpool): MeasureTotals => sumMeasures([spool]);
  return [
    [
      i18n._('Name'), i18n._('From'), i18n._('To'), spoolFieldLabel('pipe'), spoolFieldLabel('level'), spoolFieldLabel('area'),
      spoolFieldLabel('status'), spoolFieldLabel('installed'), spoolFieldLabel('removed'), spoolFieldLabel('direction'),
      format.label('length'), format.label('flowLoop'), spoolFieldLabel('other'),
    ],
    ...spools.map((spool) => [
      spool.name ?? '', spool.pointA?.nodeName ?? '', spool.pointB?.nodeName ?? '', FIELD_VALUE.pipe(spool, 'day'),
      spool.level ?? '', spool.area ?? '', FIELD_VALUE.status(spool, 'day'), FIELD_VALUE.installed(spool, 'day'),
      FIELD_VALUE.removed(spool, 'day'), directionOf(spool), format.value('length', totalsOf(spool)),
      format.value('flowLoop', totalsOf(spool)), spool.other ?? '',
    ]),
  ];
};

const FORMULA_START = /^[=+\-@\t\r]/;

// Excel runs a cell that opens like a formula, from a CSV or a paste, so free text that does is kept as text.
const asText = (cell: string | number): string => {
  const text = String(cell);
  return typeof cell === 'string' && FORMULA_START.test(text) && !Number.isFinite(Number(text)) ? `'${text}` : text;
};

export const toCsv = (table: (string | number)[][]): string => table
  .map((row) => row.map((cell) => escapeCsvValue(asText(cell))).join(','))
  .join('\r\n');

export const toTsv = (table: (string | number)[][]): string => table
  .map((row) => row.map((cell) => asText(cell).replace(/[\t\r\n]+/g, ' ')).join('\t'))
  .join('\n');
